import { useCallback, useState } from "react";
import { Notification } from "@rio-cloud/rio-uikit";
import { deleteRecurrenceGroup, deleteTransaction, updateRecurrenceGroup } from "@services/form";
import { getRecurrenceErrorLabelKey } from "@utils/apiError";
import { isMarketplaceTransaction } from "@utils/transactionSource";
import { parseCurrencyBRL } from "@utils/format";
import { useT } from "./useT";
import type { IRecurrenceGroupUpdatePayload, RecurrenceDeleteTarget, RecurrenceScope } from "@types";

interface RecurrenceDeletableRow {
  id: string;
  amount?: string;
  recurrenceId?: string | null;
  source?: string | null;
}

interface UseRecurrenceDeleteParams {
  rows: RecurrenceDeletableRow[];
  successLabelKey: string;
  errorLabelKey: string;
  onDeleted: () => void;
}

interface UseRecurrenceDeleteResult {
  target: RecurrenceDeleteTarget;
  isDeleting: boolean;
  requestDelete: (id: string) => boolean;
  confirmDelete: (scope: RecurrenceScope) => Promise<void>;
  cancelDelete: () => void;
}

export const useRecurrenceDelete = ({
  rows,
  successLabelKey,
  errorLabelKey,
  onDeleted,
}: UseRecurrenceDeleteParams): UseRecurrenceDeleteResult => {
  const t = useT();
  const [target, setTarget] = useState<RecurrenceDeleteTarget>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const requestDelete = useCallback(
    (id: string) => {
      const row = rows.find((item) => item.id === id);

      if (!row?.recurrenceId || isMarketplaceTransaction(row.source)) return false;

      setTarget({ transactionId: id, recurrenceId: row.recurrenceId });
      return true;
    },
    [rows]
  );

  const cancelDelete = useCallback(() => setTarget(null), []);

  const confirmDelete = useCallback(
    async (scope: RecurrenceScope) => {
      if (!target) return;

      setIsDeleting(true);

      try {
        if (scope === "SINGLE") {
          await deleteTransaction(target.transactionId);
        } else {
          await deleteRecurrenceGroup(target.recurrenceId, {
            scope,
            targetTransactionId: target.transactionId,
          });
        }

        setTarget(null);
        Notification.success(t(scope === "SINGLE" ? successLabelKey : "recurrence.notification.deleteSuccess"));
        onDeleted();
      } catch (error) {
        Notification.error(t(getRecurrenceErrorLabelKey(error, errorLabelKey)));
      } finally {
        setIsDeleting(false);
      }
    },
    [target, t, successLabelKey, errorLabelKey, onDeleted]
  );

  return { target, isDeleting, requestDelete, confirmDelete, cancelDelete };
};

interface UseRecurrenceEditParams<T> {
  rows: RecurrenceDeletableRow[];
  successLabelKey: string;
  errorLabelKey: string;
  onSaved: () => void;
  getAmount: (data: T) => number;
  buildPayload: (data: T, scope: RecurrenceScope, targetTransactionId: string) => IRecurrenceGroupUpdatePayload;
  submitSingle: (data: T, callback?: () => void) => Promise<void>;
}

interface UseRecurrenceEditResult<T> {
  isOpen: boolean;
  isSaving: boolean;
  requestEdit: (id: string, data: T, callback?: () => void) => boolean;
  confirmEdit: (scope: RecurrenceScope) => Promise<void>;
  cancelEdit: () => void;
}

export const useRecurrenceEdit = <T>({
  rows,
  successLabelKey,
  errorLabelKey,
  onSaved,
  getAmount,
  buildPayload,
  submitSingle,
}: UseRecurrenceEditParams<T>): UseRecurrenceEditResult<T> => {
  const t = useT();
  const [pending, setPending] = useState<{ id: string; data: T; callback?: () => void } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const requestEdit = useCallback(
    (id: string, data: T, callback?: () => void) => {
      const row = rows.find((item) => item.id === id);

      if (!row?.recurrenceId || isMarketplaceTransaction(row.source)) return false;

      setPending({ id, data, callback });
      return true;
    },
    [rows]
  );

  const cancelEdit = useCallback(() => setPending(null), []);

  const confirmEdit = useCallback(
    async (scope: RecurrenceScope) => {
      if (!pending) return;

      const row = rows.find((item) => item.id === pending.id);
      if (!row?.recurrenceId) return;

      if (scope === "SINGLE") {
        setPending(null);
        await submitSingle(pending.data, pending.callback);
        return;
      }

      if (scope === "ALL_GROUP" && getAmount(pending.data) !== parseCurrencyBRL(row.amount ?? "")) {
        Notification.error(t("recurrence.error.valueOnRealized"));
        return;
      }

      setIsSaving(true);

      try {
        await updateRecurrenceGroup(row.recurrenceId, buildPayload(pending.data, scope, pending.id));
        setPending(null);
        Notification.success(t(successLabelKey));
        pending.callback?.();
        onSaved();
      } catch (error) {
        Notification.error(t(getRecurrenceErrorLabelKey(error, errorLabelKey)));
      } finally {
        setIsSaving(false);
      }
    },
    [pending, rows, getAmount, submitSingle, buildPayload, t, successLabelKey, errorLabelKey, onSaved]
  );

  return { isOpen: pending !== null, isSaving, requestEdit, confirmEdit, cancelEdit };
};
